import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { FloatingText3D, TypingText, GlitchText } from "./Text3D";
import ParticleSystem from "./ParticleSystem";
import Timeline from "./Timeline";
import { AnimatePresence } from "framer-motion";

function GlassyT({ dark }) {
  const group = useRef();
  useFrame(({ clock }) => {
    if (group.current) {
      group.current.rotation.y = clock.getElapsedTime() * 0.7;
      group.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.3) * 0.15;
    }
  });
  return (
    <group ref={group}>
      {/* Vertical stem of T */}
      <mesh position={[0, -0.2, 0]}>
        <cylinderGeometry args={[0.18, 0.18, 1.2, 48]} />
        <meshPhysicalMaterial
          color={dark ? "#e6e9f5" : "#1b1c4e"}
          roughness={0.09}
          metalness={0.85}
          clearcoat={1}
          clearcoatRoughness={0.05}
          transmission={0.85}
          thickness={1.2}
          ior={1.6}
          reflectivity={0.95}
          opacity={0.97}
          transparent
        />
      </mesh>
      {/* Top bar of T */}
      <mesh position={[0, 0.45, 0]}>
        <boxGeometry args={[0.8, 0.22, 0.22]} />
        <meshPhysicalMaterial
          color={dark ? "#a5b4fc" : "#23244d"}
          roughness={0.13}
          metalness={0.8}
          clearcoat={1}
          clearcoatRoughness={0.07}
          transmission={0.8}
          thickness={1.1}
          ior={1.5}
          reflectivity={0.9}
          opacity={0.93}
          transparent
        />
      </mesh>
    </group>
  );
}

function GlassyNetworkNode({ dark }) {
  const group = useRef();
  useFrame(({ clock }) => {
    if (group.current) {
      group.current.rotation.y = clock.getElapsedTime() * 0.6;
      group.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.25) * 0.18;
    }
  });
  // Node positions (hexagonal-ish)
  const nodes = [
    [1.1, 0.3, 0.2],
    [-1.05, 0.25, -0.1],
    [0.7, -0.95, 0.3],
    [-0.7, -0.95, -0.3],
    [0, 1.15, -0.25],
    [0, 0.1, 1.2],
  ];
  return (
    <group ref={group}>
      {/* Central glassy sphere */}
      <mesh>
        <sphereGeometry args={[0.5, 48, 48]} />
        <meshPhysicalMaterial
          color={dark ? "#e6e9f5" : "#1b1c4e"}
          roughness={0.09}
          metalness={0.85}
          clearcoat={1}
          clearcoatRoughness={0.05}
          transmission={0.85}
          thickness={1.2}
          ior={1.6}
          reflectivity={0.95}
          opacity={0.97}
          transparent
        />
      </mesh>
      {/* Connected nodes and links */}
      {nodes.map((pos, i) => (
        <group key={i}>
          {/* Link (cylinder) */}
          <mesh position={[(pos[0]) / 2, (pos[1]) / 2, (pos[2]) / 2]}>
            <cylinderGeometry args={[0.045, 0.045, Math.sqrt(pos[0]**2 + pos[1]**2 + pos[2]**2), 32]} />
            <meshPhysicalMaterial
              color={dark ? "#a5b4fc" : "#23244d"}
              roughness={0.13}
              metalness={0.8}
              clearcoat={1}
              clearcoatRoughness={0.07}
              transmission={0.8}
              thickness={1.1}
              ior={1.5}
              reflectivity={0.9}
              opacity={0.7}
              transparent
            />
          </mesh>
          {/* Small node sphere */}
          <mesh position={pos}>
            <sphereGeometry args={[0.19, 32, 32]} />
            <meshPhysicalMaterial
              color={dark ? "#818cf8" : "#1b1c4e"}
              roughness={0.13}
              metalness={0.8}
              clearcoat={1}
              clearcoatRoughness={0.07}
              transmission={0.8}
              thickness={1.1}
              ior={1.5}
              reflectivity={0.9}
              opacity={0.93}
              transparent
            />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export default function Hero({ dark }) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section
      id="home"
      className="relative flex flex-col items-center min-h-screen h-screen py-0 text-center"
      style={{  }}
    >
      <style>{`
        @media (max-width: 640px) {
          #home {
            height: 115vh !important;
            min-height: 115vh !important;
          }
        }
      `}</style>
      <motion.div className="absolute inset-0 z-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
        <div className="absolute -top-40 -left-40 w-[650px] h-[650px] bg-gradient-to-br from-[#1b1c4e] via-indigo-400 to-indigo-200 opacity-40 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-gradient-to-tr from-indigo-200 via-indigo-400 to-[#1b1c4e] opacity-30 rounded-full blur-[100px] animate-pulse" />
      </motion.div>
      {/* Mobile Particle Effect */}
      {isMobile && (
        <div className="absolute inset-0 z-0 pointer-events-none">
          <ParticleSystem dark={dark} interactive={false} className="w-full h-full static pointer-events-none" />
        </div>
      )}
      {/* Centered hero content, takes majority of space */}
      <div className="flex flex-col items-center justify-center w-full min-h-[65vh] flex-grow z-20 px-2 sm:px-0">
        <GlitchText dark={dark} className="text-2xl xs:text-3xl sm:text-4xl md:text-6xl font-extrabold text-indigo-600 dark:text-white mb-3 sm:mb-4 text-center break-words">
              Welcome to Tech ADR
            </GlitchText>
        <FloatingText3D dark={dark} delay={0.3} className="mb-4 sm:mb-6 text-lg xs:text-xl sm:text-2xl md:text-3xl font-semibold text-white/90 text-center break-words">
            Digital Solutions for a Connected World
          </FloatingText3D>
            <TypingText 
              text="Empowering businesses with next-gen web, automation, and digital transformation. Experience the future of tech, today."
              speed={50}
              dark={dark}
          className="text-indigo-600 max-w-xs xs:max-w-sm sm:max-w-xl text-base xs:text-lg md:text-xl font-medium mb-6 sm:mb-8 text-center break-words"
        />
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 w-full max-w-xs sm:max-w-none justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            >
              Start Your Project
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 border-2 rounded-lg font-semibold transition-all duration-300 ${
                dark 
                  ? 'border-indigo-400 text-indigo-400 hover:bg-indigo-400 hover:text-white' 
                  : 'border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white'
              }`}
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
            >
              Explore Services
            </motion.button>
        </div>
      </div>
      {/* Timeline year scale at bottom, timeline data below scale, can exceed screen height */}
      <div className="w-full px-1 xs:px-2 md:px-8 pb-4 pt-0 z-30 flex flex-col items-center mb-16 sm:mb-0">
        <div className="w-full">
          <Timeline dark={dark} minimal />
                    </div>
        <div className="w-full flex flex-col items-center mt-8">
          {/* timelineData.map((item, idx) => (
            <TimelineCard key={idx} item={item} dark={dark} />
          )) */}
        </div>
      </div>
    </section>
  );
} 